import type Phaser from "phaser";
import { PLAYER } from "../../config";

export class GameUI {
  private scene: Phaser.Scene;
  private scoreText!: Phaser.GameObjects.Text;
  private healthBar!: Phaser.GameObjects.Rectangle;
  private waveText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    // Score display - LCARS style
    this.scene.add
      .rectangle(100, 25, 180, 40, 0xff9900, 0.3)
      .setStrokeStyle(2, 0xff9900);
    this.scene.add.text(20, 15, "SCORE:", {
      fontSize: "16px",
      color: "#ff9900",
      fontFamily: "Courier New",
    });
    this.scoreText = this.scene.add.text(90, 15, "0", {
      fontSize: "16px",
      color: "#ffffff",
      fontFamily: "Courier New",
    });

    // Health bar - LCARS style
    this.scene.add
      .rectangle(700, 25, 180, 40, 0x00ff00, 0.3)
      .setStrokeStyle(2, 0x00ff00);
    this.scene.add.text(620, 15, "HULL:", {
      fontSize: "16px",
      color: "#00ff00",
      fontFamily: "Courier New",
    });

    this.scene.add.rectangle(740, 25, 100, 20, 0x333333);
    this.healthBar = this.scene.add.rectangle(740, 25, 100, 20, 0x00ff00);

    // Wave indicator
    this.waveText = this.scene.add
      .text(400, 15, "SECTOR 1", {
        fontSize: "14px",
        color: "#ffcc00",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    // Controls hint
    this.scene.add
      .text(400, 580, "ARROWS/WASD: Move  |  SPACE: Phasers  |  E: Torpedoes", {
        fontSize: "14px",
        color: "#99ccff",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5)
      .setAlpha(0.8);
  }

  updateScore(score: number): void {
    this.scoreText.setText(score.toString());
  }

  updateHealth(health: number): void {
    const healthPercent = Math.max(0, health / PLAYER.MAX_HEALTH);
    this.healthBar.setScale(healthPercent, 1);
    this.healthBar.setPosition(690 + (100 * healthPercent) / 2, 25);

    // Change color based on health
    if (healthPercent > 0.6) {
      this.healthBar.setFillStyle(0x00ff00);
    } else if (healthPercent > 0.3) {
      this.healthBar.setFillStyle(0xffff00);
    } else {
      this.healthBar.setFillStyle(0xff0000);
    }
  }

  updateWave(waveNumber: number): void {
    this.waveText.setText(`SECTOR ${waveNumber}`);
  }

  showWaveAnnouncement(waveNumber: number): void {
    const announcement = this.scene.add
      .text(400, 300, `ENTERING SECTOR ${waveNumber}`, {
        fontSize: "28px",
        color: "#ffcc00",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.scene.tweens.add({
      targets: announcement,
      alpha: 0,
      duration: 2000,
      onComplete: () => announcement.destroy(),
    });
  }

  showGameOver(score: number, waveNumber: number): void {
    // Darken background
    this.scene.add.rectangle(400, 300, 800, 600, 0x000000, 0.8);

    // LCARS-style game over panel
    const panel = this.scene.add.rectangle(400, 280, 400, 300, 0x000000, 0.9);
    panel.setStrokeStyle(3, 0xff3300);

    // Red alert bars
    this.scene.add.rectangle(400, 150, 350, 8, 0xff0000);
    this.scene.add.rectangle(400, 410, 350, 8, 0xff0000);

    // Text
    this.scene.add
      .text(400, 180, "RED ALERT", {
        fontSize: "24px",
        color: "#ff0000",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.scene.add
      .text(400, 230, "VOYAGER DESTROYED", {
        fontSize: "28px",
        color: "#ff6600",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.scene.add
      .text(400, 280, `FINAL SCORE: ${score}`, {
        fontSize: "22px",
        color: "#ffffff",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.scene.add
      .text(400, 320, `SECTORS CLEARED: ${waveNumber - 1}`, {
        fontSize: "18px",
        color: "#ffcc00",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    this.scene.add
      .text(400, 380, "RELOAD PAGE TO TRY AGAIN", {
        fontSize: "14px",
        color: "#888888",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    // Borg quote
    this.scene.add
      .text(400, 450, '"Resistance is futile."', {
        fontSize: "16px",
        color: "#00ff00",
        fontFamily: "Courier New",
        fontStyle: "italic",
      })
      .setOrigin(0.5);
  }
}
